// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartMapContent.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartMapContent model
 */

import { extendObservable, reaction, action, toJS, observable, computed } from 'mobx';
import {
  cloneDeep,
  getOr,
  flatMap,
  forEach,
  flow,
  get,
  toNumber,
  values,
  has,
  find,
  merge,
  isUndefined,
  isEmpty,
  omit,
  map,
  set,
  findIndex,
  assign,
  first,
  last,
  uniq,
  concat,
  isNil
} from 'lodash/fp';

import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import ReportPartContent from './Izenda.ReportPartContent';
import ReportUtils from './Izenda.ReportUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import ChartDefaultConfig from 'logic/data/Izenda.ChartDefaultConfig';
import {
  DataLabels,
  DEFAULT_SETTING_VALUE,
  MAP_TYPES,
  ContainerName,
  DefaultChartType,
  DefaultFunction,
  DefaultGEOBackground,
  DefaultContinent,
  DefaultCountry,
  PointsOptionsOrder as POINTS_OPTIONS_ORDER,
  PointsOptionsForShading as POINTS_OPTIONS_FOR_SHADING,
  PointsOptionsForBubbleAll as POINTS_OPTIONS_FOR_BUBBLE_ALL,
  FunctionConfig as FUNCTION_CONFIG,
  DrilldownLevels as DRILLDOWN_LEVELS,
  DrilldownOrders as DRILLDOWN_ORDERS
} from 'logic/data/Izenda.ReportPartMapConstants';
import MapCollection from 'logic/data/Izenda.MapCollectionConstants';
import { exportClass } from 'share/Izenda.ServiceProvider';
import uiState from 'logic/Izenda.UiState';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';
import { PERMISSION_KEY } from 'logic/data/Izenda.AuthorizationConstants';
import { Highmaps as HighmapsVizName } from 'share/components/charting/highcharts/Izenda.HighchartsVizNames';
import GoogleMapsVizName from 'share/components/charting/googlemaps/Izenda.GoogleMapsVizName';

const hasElement = ReportPartUtils.hasElement;
const getElements = ReportPartUtils.getElements;
const getContainerByField = ReportPartUtils.getContainerByField;
const populateReportChartContainer = ReportPartUtils.populateReportChartContainer;

@exportClass('ReportPartMapContent')
@tracker
class ReportPartMapContent extends ReportPartContent {
  @observable isCrossFiltering = false;
  @observable type = null;
  @observable title = null;
  @observable description = null;
  @observable
  properties = {
    reportPartState: {
      drilldownInfo: [],
      activeSerie: {},
      activePointOption: {},
      pointOptionsList: []
    },
    chartType: !isEmpty(uiState.googleApiKey) ? MAP_TYPES.Google : MAP_TYPES.World,
    vizEngine: !isEmpty(uiState.googleApiKey) ? GoogleMapsVizName : HighmapsVizName,
    colorTheme: undefined,
    isDefaultTheme: true,
    continentInfo: {},
    countryInfo: {},
    stateInfo: {},
    commonOptions: {},
    optionByType: {},
    view: {
      showLabels: false,
      dataRefreshInterval: {
        enable: false,
        updateInterval: 0,
        isAll: true,
        latestRecord: 0
      }
    }
  };

  // Do not observe this property
  vizState = {
    zoomLevel: null,
    centerPoint: { lat: 0, lng: 0 }
  };

  constructor(data) {
    super(REPORT_PART_TYPE.Map);

    this.isEnableGoogleAddress = new SecurityManager().checkPrivileges([
      PERMISSION_KEY.ReportsReportPartTypesEnableGoogleAddress
    ]);

    this.selectedExpandedLevel = 0;
    const props = {};

    // important - these containers save list of fields to get data and render report
    ReportPartUtils.addContainers(props, ContainerName.COUNTRY);
    ReportPartUtils.addContainers(props, ContainerName.STATE);
    ReportPartUtils.addContainers(props, ContainerName.COUNTY);
    ReportPartUtils.addContainers(props, ContainerName.CITY);
    ReportPartUtils.addContainers(props, ContainerName.POSTAL_CODE);
    ReportPartUtils.addContainers(props, ContainerName.ADDRESS);
    ReportPartUtils.addContainers(props, ContainerName.LATITUDE);
    ReportPartUtils.addContainers(props, ContainerName.LONGTITUDE);
    ReportPartUtils.addContainers(props, ContainerName.SHADING_VALUES);
    ReportPartUtils.addContainers(props, ContainerName.BUBBLE_VALUES);
    this.addCustomContainer(ContainerName.THRESHOLDS);
    this.addCustomContainer(ContainerName.THRESHOLDS_SUBTOTAL);

    this.properties.commonOptions = cloneDeep(this.defaultCommonOptions);
    this.properties.optionByType = cloneDeep(this.defaultOptionsByType);

    extendObservable(this, props);

    data && this.readData(data);

    // Reaction is should only triggered after the report part map content properly loads it data.
    // If doing this before initial loading data, some initial properties reset in resetDrillDownInfo.
    const mapReactions = () => {
      // trick - access num elements of each container to trigger change
      // every have action on component (add/delete field, visible/hidden element)
      this.drilldownReaction = reaction(() => {
        let name;
        let totalNum = 0;
        for (name in ContainerName) {
          if (ContainerName.hasOwnProperty(name)) {
            totalNum += getOr(0, 'numElement', this[ContainerName[name]]);
          }
        }

        return totalNum;
      }, this.resetDrilldownInfo.bind(this));

      // add action to certain field: sub report, embeded...>> turn [Show Value Labels] to ON
      this.fieldActions = reaction(
        () => {
          let numActions = 0;
          const fields = flatMap(item => (item.elements ? item.elements.slice() : []), this.containerCollection);

          forEach(field => {
            numActions += flow(
              get('numberOfPointActions'),
              toNumber
            )(field);
          }, fields);

          return {
            numActions: numActions
          };
        },
        re => {
          return re.numActions > 0 && (this.properties.optionByType.izValueLabel = true);
        }
      );
    };
    mapReactions.call(this);

    this.cleanDirty();
  }

  @computed
  get chartType() {
    return this.properties.chartType;
  }

  @computed
  get drilldownInfo() {
    return get('properties.reportPartState.drilldownInfo', this);
  }

  // use to show/hide fields which need to draw chart - do not save
  @computed
  get activeSerie() {
    return get('properties.reportPartState.activeSerie', this);
  }

  @computed
  get activePointOption() {
    return get('properties.reportPartState.activePointOption', this);
  }

  @computed
  get pointOptionsList() {
    return get('properties.reportPartState.pointOptionsList', this);
  }

  @computed
  get containerCollection() {
    const containers = [];

    values(ContainerName).forEach(containerName => {
      this[containerName] && containers.push(this[containerName]);
    });

    return containers;
  }

  @computed
  get isBeingBuild() {
    const activePointOption = this.activePointOption;
    const pointType = get('pointType', activePointOption);
    let isBeingBuildShading, isBeingBuildBubble;

    if (POINTS_OPTIONS_FOR_BUBBLE_ALL.indexOf(pointType) > -1 && hasElement.call(this, ContainerName.BUBBLE_VALUES)) {
      isBeingBuildBubble = true;
    }

    if (POINTS_OPTIONS_FOR_SHADING.indexOf(pointType) > -1 && hasElement.call(this, ContainerName.SHADING_VALUES)) {
      isBeingBuildShading = true;
    }

    return isBeingBuildShading || isBeingBuildBubble;
  }

  @computed
  get dataRefreshInterval() {
    return get('view.dataRefreshInterval', this.properties);
  }

  @computed
  get optionalParams() {
    return this.getDrilldownInfo();
  }

  @computed
  get serieChartList() {
    const series = [];
    const containerNames = [];
    const pointType = get('pointType', this.activePointOption);

    if (POINTS_OPTIONS_FOR_BUBBLE_ALL.indexOf(pointType) > -1) {
      containerNames.push(ContainerName.BUBBLE_VALUES);
    }

    if (POINTS_OPTIONS_FOR_SHADING.indexOf(pointType) > -1) {
      containerNames.push(ContainerName.SHADING_VALUES);
    }

    containerNames.forEach(containerName => {
      return this[containerName].visibleElement.forEach(field => {
        series.push(field);
      });
    });

    return series;
  }

  @computed
  get activeElmsByContainers() {
    const containers = {};
    const activeSerieKey = get('fieldName', this.activeSerie);
    const pointType = get('pointType', this.activePointOption);

    // point options
    if ([ContainerName.LATITUDE, ContainerName.LONGTITUDE].indexOf(pointType) > -1) {
      containers[ContainerName.LATITUDE] = getElements(this[ContainerName.LATITUDE]);
      containers[ContainerName.LONGTITUDE] = getElements(this[ContainerName.LONGTITUDE]);
    } else {
      // add country container for state level
      // to filter state belong to drilldown country
      switch (pointType) {
        case ContainerName.STATE:
          containers[ContainerName.COUNTRY] = getElements(this[ContainerName.COUNTRY]);
          break;
        case ContainerName.COUNTY:
          containers[ContainerName.COUNTRY] = getElements(this[ContainerName.COUNTRY]);
          containers[ContainerName.STATE] = getElements(this[ContainerName.STATE]);
          break;
        case ContainerName.CITY:
          containers[ContainerName.COUNTRY] = getElements(this[ContainerName.COUNTRY]);
          containers[ContainerName.STATE] = getElements(this[ContainerName.STATE]);
          containers[ContainerName.COUNTY] = getElements(this[ContainerName.COUNTY]);
          break;
        case ContainerName.POSTAL_CODE:
          containers[ContainerName.COUNTRY] = getElements(this[ContainerName.COUNTRY]);
          containers[ContainerName.STATE] = getElements(this[ContainerName.STATE]);
          containers[ContainerName.COUNTY] = getElements(this[ContainerName.COUNTY]);
          containers[ContainerName.CITY] = getElements(this[ContainerName.CITY]);
          break;
        case ContainerName.ADDRESS:
          containers[ContainerName.COUNTRY] = getElements(this[ContainerName.COUNTRY]);
          containers[ContainerName.STATE] = getElements(this[ContainerName.STATE]);
          containers[ContainerName.COUNTY] = getElements(this[ContainerName.COUNTY]);
          containers[ContainerName.CITY] = getElements(this[ContainerName.CITY]);
          containers[ContainerName.POSTAL_CODE] = getElements(this[ContainerName.POSTAL_CODE]);
          break;
      }

      pointType && (containers[pointType] = getElements(this[pointType]));
    }

    if (POINTS_OPTIONS_FOR_BUBBLE_ALL.indexOf(pointType) > -1) {
      containers[ContainerName.BUBBLE_VALUES] = getElements(this[ContainerName.BUBBLE_VALUES], activeSerieKey);
    }

    if (POINTS_OPTIONS_FOR_SHADING.indexOf(pointType) > -1) {
      containers[ContainerName.SHADING_VALUES] = getElements(this[ContainerName.SHADING_VALUES], activeSerieKey);
    }

    return containers;
  }

  @computed
  get hasDrillDown() {
    return !!this.drilldownInfo.length;
  }

  @computed
  get currentDrilldownLevel() {
    const lastDrilldownLevel = last(this.drilldownInfo);

    if (lastDrilldownLevel) {
      return get('mapInfo.mapLevel', lastDrilldownLevel);
    } else {
      return this.mapLevelBySelectedChartType;
    }
  }

  @computed
  get mapLevelBySelectedChartType() {
    const props = this.properties;

    if (get('geoFile', props.stateInfo)) {
      return DRILLDOWN_LEVELS.STATE;
    } else if (get('geoFile', props.countryInfo)) {
      return DRILLDOWN_LEVELS.COUNTRY;
    } else if (get('geoFile', props.continentInfo)) {
      return DRILLDOWN_LEVELS.CONTINENT;
    }
    return DRILLDOWN_LEVELS.WORLD;
  }

  @computed
  get backgroundMap() {
    const properties = this.properties;
    let backgroundInfo, mapLevel, backgroundMap;

    if (!this.drilldownInfo.length) {
      if (has('geoFile', properties.stateInfo)) {
        backgroundInfo = properties.stateInfo;
        mapLevel = DRILLDOWN_LEVELS.STATE;
      } else if (has('geoFile', properties.countryInfo)) {
        backgroundInfo = properties.countryInfo;
        mapLevel = DRILLDOWN_LEVELS.COUNTRY;
      } else if (has('geoFile', properties.continentInfo)) {
        backgroundInfo = properties.continentInfo;
        mapLevel = DRILLDOWN_LEVELS.CONTINENT;
      }
    } else {
      const lastDrilldownLevel = last(this.drilldownInfo);

      backgroundInfo = get('mapInfo.backgroundMap', lastDrilldownLevel);
      mapLevel = get('mapInfo.mapLevel', lastDrilldownLevel);
    }

    backgroundMap = get('geoFile', backgroundInfo);

    return {
      backgroundMap: backgroundMap || DefaultGEOBackground,
      mapLevel: mapLevel || DRILLDOWN_LEVELS.WORLD
    };
  }

  @computed
  get reportPartStyle() {
    return this.properties.chartType;
  }

  @computed
  get vizEngine() {
    return this.properties.vizEngine;
  }

  get defaultVizEngine() {
    return GoogleMapsVizName;
  }

  @action
  setVizEngine(value: string) {
    this.properties.vizEngine = value;
  }

  @computed
  get isShowMetric() {
    return !isEmpty(this.serieChartList) && this.serieChartList.length > 1;
  }

  /**
   * Determine whether the report part content automatically sizes its container or not
   */
  get autoSize() {
    return false;
  }

  /**
   * Validate function based on container
   * @method validateFunction
   * @param  {Object} container    container need validateFunction
   * @param  {Object} functionInfo Function need validate
   * @return {Object}              validate result
   */
  validateFunction(container: any, functionInfo: any): any {
    if (!functionInfo) {
      return {
        status: false,
        useDefaultFunction: false
      };
    }

    return {
      status: true
    };
  }

  /**
   * Get Default Function And Format for field in container
   * @method getDefaultFunctionFormat
   * @param  {Object} container Container have element need get default dataType
   * @param  {string} dataType  Data Type for field
   * @return {Object}           default data
   */
  getDefaultFunctionFormat(container: any, dataType: string): any {
    const defaultFunctions = DefaultFunction;
    const byContainer = defaultFunctions[container.name || ''] || {};
    return ReportPartUtils.getDefaultFunction(byContainer, dataType);
  }

  /**
   * Read data from api/create report part to update data
   * @param {Object} content [data from api/create report part]
   * @param {boolean} isCopy
   * @param {boolean} viewMode
   * @param {boolean} editMode
   */
  @action
  readData(content: any, isCopy: boolean, viewMode: boolean, editMode: boolean): void {
    content = content || {};
    const properties = content.properties || {};
    this.vizState = content.vizState || this.vizState;
    this.type = content.type;
    this.isCrossFiltering = content.isCrossFiltering;

    this.buildPointOptionsData(get('elements', content[ContainerName.POINT_OPTIONS]), false, viewMode, editMode);
    this[ContainerName.SHADING_VALUES] = populateReportChartContainer(
      content[ContainerName.SHADING_VALUES],
      ContainerName.SHADING_VALUES
    );
    this[ContainerName.BUBBLE_VALUES] = populateReportChartContainer(
      content[ContainerName.BUBBLE_VALUES],
      ContainerName.BUBBLE_VALUES
    );

    // Populate custom container if defined
    const customContainersContent = get('customContainers', content);
    if (customContainersContent && customContainersContent.length > 0) {
      const newCustomContainers = this.customContainers.map(container => {
        const { name: containerName } = container;
        const customContent = find(['name', containerName], customContainersContent);
        return customContent ? ReportPartUtils.populateReportChartContainer(customContent, containerName) : container;
      });
      this.customContainers.replace(newCustomContainers);
    }

    this.settings = content.settings;

    // report part properties
    this.setChartType(properties.chartType);
    this.setReportPartProperties(content.properties);
    this.setActiveSerie(null, find({ fieldName: content.selectedMetric }, this.serieChartList));

  }

  /**
   * Set data by data key
   * @param {string} key
   * @param {Object} newVal
   */
  @action
  setDataByKey(key: string, newVal: any): void {
    this[key] = newVal;
    this.markDirty();
  }

  /**
   * Get data by data key
   * @param {string} key
   */
  getDataByKey(key: string): any {
    return this[key];
  }

  /**
   * Clean dirty status.
   */
  cleanDirty(): void {
    this.resetDirty();
  }

  /**
   * Get Map's options
   * @returns {Object}
   */
  getMapOptions(): any {
    const props = toJS(this.properties);
    const backgroundMap = this.backgroundMap;

    const mapOptions = merge(
      props,
      toJS({
        ...this.vizState,
        // chartType: 'Map',
        commonOptions: {
          izendaOptions: {
            backgroundMapName: backgroundMap.backgroundMap,
            mapLevel: backgroundMap.mapLevel,
            activePointOption: this.activePointOption,
            dataLabelKey: getOr('', backgroundMap.mapLevel, DataLabels)
          }
        }
      })
    );

    this.removeBackgroundColorForGoogleMaps(mapOptions);
    return mapOptions;
  }

  /**
   * Remove background color when map type is Google Maps
   * @param {Object} mapOptions
   */
  removeBackgroundColorForGoogleMaps(mapOptions: Object) {
    if (this.chartType === MAP_TYPES.Google) {
      mapOptions.commonOptions['izEntire.backgroundColor'] =
        DEFAULT_SETTING_VALUE.backGroundColorDefault['izEntire.backgroundColor'];
      mapOptions.commonOptions['izPlot.backgroundColor'] =
        DEFAULT_SETTING_VALUE.backGroundColorDefault['izPlot.backgroundColor'];
    }
  }

  /**
   * Get properties of report part, if propKey is null, return all properties
   * @param  {string} propKey
   * @return {Object}
   */
  getReportPartProperties(propKey: string): any {
    const props = toJS(this.properties) || {};

    if (!propKey) {
      return props;
    }

    let definedVal = get(propKey, props.reportPartState);
    definedVal = isUndefined(definedVal)
      ? getOr(get(propKey, props.optionByType || {}), propKey, props.commonOptions || {})
      : definedVal;
    definedVal = isUndefined(definedVal) ? get(propKey, props.view) : definedVal;
    definedVal = isUndefined(definedVal) ? get(propKey, props) : definedVal;

    return definedVal;
  }

  /**
   * Set properties of report part
   * @param {Object}
   */
  @action
  setReportPartProperties(newProps: any): void {
    const preChartType = this.chartType;
    const newReportPartState = newProps.reportPartState || {};
    const reportPartState = this.properties.reportPartState;

    // mobx limitation (v2.4.4) - cannot extend observable array
    if (newReportPartState.drilldownInfo) {
      reportPartState.drilldownInfo = newReportPartState.drilldownInfo;
    }

    if (newReportPartState.pointOptionsList) {
      reportPartState.pointOptionsList = newReportPartState.pointOptionsList;
    }
    // To keep this "activeSerie" property from disappearance
    if (!newReportPartState.activeSerie) {
      newReportPartState.activeSerie = {};
    }

    if (!isUndefined(newProps.colorTheme) || newProps.isDefaultTheme) {
      this.properties.isDefaultTheme = newProps.isDefaultTheme;
      this.properties.colorTheme = newProps.colorTheme;
    }

    extendObservable(this.properties, newProps);
    this.convertChartType(preChartType, this.chartType);
    this.markDirty();
  }

  /**
   * Get dirty status.
   * @returns {boolean}
   */
  checkDirty(): boolean {
    const dirtyElements = ReportUtils.dirtyReportPartElementField(this.containerCollection);
    const properties = this.properties;

    return this.isDirty || !isEmpty(dirtyElements) || (properties && properties.checkDirty && properties.checkDirty());
  }

  /**
   * Set Chart type
   * @param {string} nextType
   */
  @action
  setChartType(nextType: string): void {
    const previousType = this.properties.chartType;
    this.properties.chartType = nextType;
    this.setVizEngineByCurrentChartType();
    this.convertChartType(previousType, nextType);
    this.markDirty();
  }

  /**
   * Convert chart type
   * @param  {string} previousType
   * @param  {string} nextType
   * @return {boolean|undefined}
   */
  @action
  convertChartType(previousType: string, nextType: string): any {
    if (!previousType) {
      return false;
    }

    if (previousType === nextType) {
      return false;
    }

    const props = this.properties;

    switch (nextType) {
      case MAP_TYPES.World:
      case MAP_TYPES.Google: {
        props.continentInfo = {};
        props.countryInfo = {};
        props.stateInfo = {};
        break;
      }

      case MAP_TYPES.Continent: {
        props.continentInfo = MapCollection.getMapInfo(DefaultContinent);
        props.continent = DefaultContinent;

        props.country = '';
        props.countryInfo = {};

        props.state = '';
        props.stateInfo = {};
        break;
      }

      case MAP_TYPES.Country: {
        props.continent = '';
        props.continentInfo = {};

        props.country = DefaultCountry;
        props.countryInfo = MapCollection.getMapInfo(DefaultCountry);

        props.state = '';
        props.stateInfo = {};
        break;
      }
    }

    this.resetDrilldownInfo();

    this.markDirty();
  }

  /**
   * Build data for save
   * @returns {Object}
   */
  @action
  buildDataForSave(): any {
    const allPointContainers = POINTS_OPTIONS_ORDER;
    const mapContent = toJS(omit(allPointContainers, this));
    mapContent[ContainerName.POINT_OPTIONS] = toJS(this.buildPointOptionsData(allPointContainers, true));
    mapContent.selectedMetric = get('activeSerie.fieldName', this);

    return mapContent;
  }

  /**
   * Build point option data
   * @param {Object} pointContainers
   * @param {boolean} isZipData
   * @param {boolean} viewMode
   * @param {boolean} editMode
   */
  @action
  buildPointOptionsData(pointContainers: any, isZipData: boolean, viewMode: boolean, editMode: boolean): any {
    // Do not build point when point containers is empty
    if (!pointContainers) {
      return;
    }

    if (this.chartType === MAP_TYPES.Google && !this.isEnableGoogleAddress && (!viewMode || !!editMode)) {
      let index = -1;
      do {
        index = findIndex(p => get('properties.pointOptionType', p) === ContainerName.ADDRESS, pointContainers);
        if (index !== -1) {
          pointContainers.splice(index, 1);
          this.markDirty();
        }
      } while (index !== -1);
    }

    if (isZipData) {
      return combineAllPointOptions.apply(this);
    } else {
      return separateAllPointOptions.apply(this);
    }

    // combine all of elements of point-options container to 1 container with name [ContainerName.POINT_OPTIONS]
    function combineAllPointOptions() {
      let pointsOptions = {};
      let position = 1;

      ReportPartUtils.addContainers(pointsOptions, ContainerName.POINT_OPTIONS);

      pointsOptions = pointsOptions[ContainerName.POINT_OPTIONS];

      // combine all of elements of point-options container to 1 container with name [ContainerName.POINT_OPTIONS]
      pointContainers.map(containerName => {
        map(elm => {
          pointsOptions.elements.push(
            flow(
              set('position', position),
              set('properties.pointOptionType', containerName)
            )(elm)
          );
          position++;
        }, this[containerName].elements);
      });

      return pointsOptions;
    }

    function separateAllPointOptions() {
      // TODO
      let pointOptionType;

      pointContainers.forEach(elm => {
        pointOptionType = get('properties.pointOptionType', elm);
        if (!pointOptionType) {
          return;
        }
        this[pointOptionType] = populateReportChartContainer({ elements: [elm] }, pointOptionType);
      });
    }
  }

  /**
   * Set active serie key, used to show/hide serie chart in metric dropdown
   * @param {string} activeSerieKey
   * @param {Object} activeSerieInfo
   */
  @action
  setActiveSerie(activeSerieKey: string, activeSerieInfo: any): void {
    const reportPartState = get('properties.reportPartState', this);
    reportPartState.activeSerie = activeSerieInfo;
    this.markDirty();
  }

  /**
   * Set active point option
   * @param {string} activePointOptionKey
   * @param {Object} activePointOption
   */
  @action
  setActivePointOption(activePointOptionKey: string, activePointOption: any): void {
    const reportPartState = get('properties.reportPartState', this);
    reportPartState.activePointOption = activePointOption;
    this.markDirty();
  }

  /**
   * Enable show value labels
   * It's used when one of these customURL, embeddedJavascript, subreport are configured
   */
  enableValueLabel(): void {
    this.properties.optionByType.izValueLabel = true;
  }

  /**
   * Update drilldown info
   * @param {Object} drilldown
   */
  @action
  updateDrilldownInfo(drilldown: any): any {
    this.markDirty();

    if (!drilldown) {
      // move to top level
      return this.resetDrilldownInfo();
    }

    const drilldownInfo = this.drilldownInfo;
    const field = drilldown.field;
    const point = drilldown.point;
    const mapInfo = drilldown.mapInfo;
    let allowToDrilldown;

    const deleteChildLevel = (fromLevel: any): any => {
      const fromLevelIndex = findIndex(drilldown => get('mapInfo.mapLevel', drilldown) === fromLevel, drilldownInfo);

      return fromLevelIndex > -1
        ? drilldownInfo.splice(fromLevelIndex + 1, drilldownInfo.length - fromLevelIndex - 1)
        : null;
    };

    const addNewLevel = (currentDrilldownLevel: string, field: any, point: any): any => {
      const currentDrilldownOrder = DRILLDOWN_ORDERS.indexOf(currentDrilldownLevel);
      const nextLevel = DRILLDOWN_ORDERS[currentDrilldownOrder + 1];
      const nextMapInfo = nextLevel && MapCollection.searchMapInfoByMapLevel(nextLevel, get('pointInfo', point));

      if (nextMapInfo) {
        return drilldownInfo.push({
          field: field,
          point: point,
          mapInfo: {
            mapLevel: nextLevel,
            backgroundMap: nextMapInfo
          }
        });
      }

      return null;
    };

    if (mapInfo) {
      // drill up
      allowToDrilldown = deleteChildLevel(mapInfo.mapLevel);
    } else {
      // drill down
      allowToDrilldown = addNewLevel(this.currentDrilldownLevel, field, point);
    }

    if (allowToDrilldown) {
      return this.updatePointOptionsList(!!mapInfo);
    } else {
      return null;
    }
  }

  /**
   * Reset drilldown info
   */
  @action
  resetDrilldownInfo(): void {
    const reportPartState = get('properties.reportPartState', this);
    reportPartState.drilldownInfo.clear();
    this.updatePointOptionsList(true);
    this.markDirty();
  }

  /**
   * After insert field to container
   */
  @action
  afterInsertFieldToContainer(): void {
    this.resetDrilldownInfo();
    this.markDirty();
    return null;
  }

  /**
   * Get drilldown info
   */
  @action
  getDrilldownInfo(): void {
    const drilldownInfo = this.drilldownInfo;
    const filters = [];
    let expandedLevel = 0;
    const pointType = get('pointType', this.activePointOption);
    const pointTypeIndex = POINTS_OPTIONS_ORDER.indexOf(pointType);

    let pointName, drilldownByField;

    for (let i = 0; i < pointTypeIndex; i++) {
      pointName = POINTS_OPTIONS_ORDER[i];
      if (hasElement(this[pointName])) {
        expandedLevel++;

        drilldownByField = getDrilldownInfoOfField(drilldownInfo, this[pointName].visibleElement[0]);
        drilldownByField &&
          filters.push({
            key: get('point.nameField.field.name', drilldownByField),
            value: get('point.pointName', drilldownByField)
          });
      }
    }

    // for latitude options, we have to get data for both latitude & longtitude
    if (pointType === ContainerName.LATITUDE) {
      expandedLevel += 1;
    }

    this.selectedExpandedLevel = expandedLevel;

    return {
      filters: filters,
      expandedLevel: expandedLevel
    };

    function getDrilldownInfoOfField(drilldownInfo, field) {
      return find(
        drilldown =>
          get('mapInfo.mapLevel', drilldown) !== DRILLDOWN_LEVELS.CONTINENT &&
          get('point.nameField.field.name', drilldown) === field.fieldName,
        drilldownInfo
      );
    }
  }

  /**
   * Get drilled down points.
   * @return {Array}
   */
  getPoints(): Array {
    const drillDownInfo = getOr([], 'drilldownInfo', this);

    return map(level => {
      const point = getOr({}, 'point', level);
      return assign(level, {
        value: point.name || get('mapInfo.backgroundMap.label', level)
      });
    }, drillDownInfo);
  }

  /**
   * Get Crossfilter Info.
   * @return {Array}
   */
  getCrossFilteringInfo(): Array {
    let points = this.getPoints();

    let filters = [];
    if (isEmpty(points)) {
      return filters;
    }

    forEach(p => {
      const fieldName = get('point.nameField.field.name', p);
      !isEmpty(p.mapInfo.backgroundMap.value) &&
        fieldName &&
        filters.push({
          key: fieldName,
          value: p.value,
          field: get('point.drillingDownField', p)
        });
    }, points);

    return filters;
  }

  /**
   * Reset point options to first element
   */
  @action
  updateActivePointOptions(): void {
    this.setActivePointOption(null, first(this.pointOptionsList));
    this.markDirty();
  }

  /**
   * Update Point option list
   * @method updatePointOptionsList
   * @param  {boolean} isDrillUp    flag to notify user drill up. Back the point option to the top level.
   */
  @action
  updatePointOptionsList(isDrillUp: boolean): any {
    const pointList = [];
    const activeSerie = this.activeSerie;
    const activeContainer = activeSerie && getContainerByField(this.containerCollection, activeSerie);
    const currentDrilldownLevel = this.currentDrilldownLevel;
    const showingBackgroundMapInfo = flow(
      last,
      get('mapInfo.backgroundMap')
    )(this.drilldownInfo);
    let hasShadingValues, hasBubbleValues;

    const updatePointList = (newList: Array<any>): void => {
      const activePointOptionType = get('pointType', this.activePointOption);
      const isValidActivePointOptions = find({ pointType: activePointOptionType }, newList);

      this.pointOptionsList.replace(newList);
      // Reset the point action to the top level when :
      // 1. The current option is not existed in options list.
      // 2. User drill up map.
      (!isValidActivePointOptions || isDrillUp) && this.updateActivePointOptions();
      this.markDirty();
    };

    const addField = (container: any, source: any, containerName: string): void => {
      forEach(field => {
        source.push({
          label: field.fieldName,
          value: field.fieldName,
          field: field,
          pointType: containerName,
          pointTypeName:
            containerName === ContainerName.LATITUDE
              ? [ContainerName.LATITUDE, ContainerName.LONGTITUDE].join('_')
              : containerName
        });
      }, container.visibleElement);
    };

    const getPointOptionsForShading = (currentDrilldownLevel: string, showingBackgroundMapInfo: any): Array<any> => {
      let pointOptions = [];

      if (this.chartType === MAP_TYPES.Google) {
        pointOptions = [ContainerName.COUNTRY, ContainerName.STATE, ContainerName.COUNTY];
      } else {
        switch (currentDrilldownLevel) {
          case DRILLDOWN_LEVELS.WORLD:
          case DRILLDOWN_LEVELS.CONTINENT:
            pointOptions = [ContainerName.COUNTRY];
            break;
          case DRILLDOWN_LEVELS.COUNTRY:
            pointOptions = [ContainerName.STATE];

            // only show if background-info has county-map
            if (get('others.COUNTY_MAP', showingBackgroundMapInfo)) {
              pointOptions.push(ContainerName.COUNTY);
            }
            break;
          case DRILLDOWN_LEVELS.STATE:
            pointOptions = [ContainerName.COUNTY];
            break;
          default:
          // do nothing
        }
      }

      return pointOptions;
    };

    const pointOptionsForBubble = (currentDrilldownLevel: string, showingBackgroundMapInfo: any): Array<any> => {
      if (!hasBubbleValues) {
        return [];
      }

      // Show state point options only when drilldown level is not higher than state
      const pointOptions =
        this.chartType === MAP_TYPES.Google
          ? [
              ContainerName.COUNTRY,
              ContainerName.STATE,
              ContainerName.COUNTY,
              ContainerName.CITY,
              ContainerName.POSTAL_CODE,
              ContainerName.ADDRESS
            ]
          : currentDrilldownLevel === DRILLDOWN_LEVELS.STATE
          ? [ContainerName.CITY, ContainerName.POSTAL_CODE]
          : [ContainerName.STATE, ContainerName.CITY, ContainerName.POSTAL_CODE];

      if (this.chartType === MAP_TYPES.Google && !this.isEnableGoogleAddress) {
        const index = findIndex(p => p === ContainerName.ADDRESS, pointOptions);
        index !== -1 && pointOptions.splice(index, 1);
      }

      if (hasElement.call(this, ContainerName.LONGTITUDE) && hasElement.call(this, ContainerName.LATITUDE)) {
        pointOptions.push(ContainerName.LATITUDE);
      }

      return pointOptions;
    };

    // show all metric
    if (!activeContainer) {
      hasShadingValues = hasElement.call(this, ContainerName.SHADING_VALUES);
      hasBubbleValues = hasElement.call(this, ContainerName.BUBBLE_VALUES);
    } else {
      hasShadingValues = get('name', activeContainer) === ContainerName.SHADING_VALUES;
      hasBubbleValues = get('name', activeContainer) === ContainerName.BUBBLE_VALUES;
    }

    if (!hasShadingValues && !hasBubbleValues) {
      return updatePointList([]);
    }

    const pointOptionsShading = getPointOptionsForShading.call(this, currentDrilldownLevel, showingBackgroundMapInfo);
    const pointOptionsBubble = pointOptionsForBubble.call(this, currentDrilldownLevel);

    // remove duplicated container
    let pointContainerNames;

    if (hasShadingValues && !hasBubbleValues) {
      pointContainerNames = pointOptionsShading;
    } else {
      pointContainerNames = uniq(concat(pointOptionsShading, pointOptionsBubble));
    }

    // combine containers to an array
    pointContainerNames.forEach(name => {
      addField(this[name], pointList, name);
    });

    return updatePointList(pointList);
  }

  /**
   * check if function is compatible with element
   * @method checkedCompatibleFunction
   * @param  {Object} container    Container that element belong
   * @param  {Object} element      Element need to validate
   * @param  {Object} functionInfo Function infomation
   * @return {boolean}             Result
   */
  checkedCompatibleFunction(container: any, element: any, functionInfo: any): boolean {
    if (!container) {
      return true;
    }

    if (element.field.hasAggregatedFunction) {
      return ReportPartUtils.checkedCompatibleFunctionHasAggregatedFunction(functionInfo);
    }

    const isValues = [ContainerName.SHADING_VALUES, ContainerName.BUBBLE_VALUES].indexOf(container.name) > -1;
    const functionConfig = FUNCTION_CONFIG[isValues ? 'VALUES' : 'POINT_OPTIONS'];

    return ReportPartUtils.checkCompatibleFunctionCommon(functionInfo, element, functionConfig);
  }

  /**
   * Check need to break page when printing.
   */
  checkBreakPage(): boolean {
    return false;
  }

  /**
   * Reset drilldown info whenever reorder field
   */
  @action
  changePositionField(): void {
    this.resetDrilldownInfo();
  }

  /**
   * Check if this field can have state Unsort
   */
  checkCanUnsort(): boolean {
    return false;
  }

  /**
   * Get container name
   * @param {Object} container
   * @returns {string}
   */
  getContainerName(container: any): string {
    return container.name === ContainerName.SHADING_VALUES || container.name === ContainerName.BUBBLE_VALUES
      ? container.name
      : ContainerName.POINT_OPTIONS;
  }

  /**
   * Reset border setting to default value
   */
  @action
  resetBorderDefaultValue(): void {
    this.properties.commonOptions['izEntire.borderColor'] = DEFAULT_SETTING_VALUE.borderDefault['izEntire.borderColor'];
    this.properties.commonOptions['izEntire.borderWidth'] = DEFAULT_SETTING_VALUE.borderDefault['izEntire.borderWidth'];
    this.properties.commonOptions['izEntire.borderStyle'] = DEFAULT_SETTING_VALUE.borderDefault['izEntire.borderStyle'];
    this.markDirty();
  }

  /**
   * Reset background color setting to default value
   */
  @action
  resetBackgroundColorDefaultValue(): void {
    this.properties.commonOptions['izEntire.backgroundColor'] =
      DEFAULT_SETTING_VALUE.backGroundColorDefault['izEntire.backgroundColor'];
    this.properties.commonOptions['izPlot.backgroundColor'] =
      DEFAULT_SETTING_VALUE.backGroundColorDefault['izPlot.backgroundColor'];
    this.markDirty();
  }

  /**
   * Reset refresh interval setting to default value
   * @returns {Object}
   */
  @action
  resetRefreshIntervalValue(): any {
    this.properties.view.dataRefreshInterval = DEFAULT_SETTING_VALUE.refreshIntervalDefault;
    this.markDirty();
    return this.properties.view.dataRefreshInterval;
  }

  /**
   * Reset legend setting to default value
   * @returns {Object}
   */
  @action
  resetLegendDefaultValue(): any {
    this.properties.commonOptions['izLegend.visibility'] = false;
    this.markDirty();
    return this.properties.commonOptions;
  }

  /**
   * Reset zoom setting to default value
   * @returns {Object}
   */
  @action
  resetZoomDefaultValue(): any {
    this.properties.optionByType['izMapNavigation.enabled'] = false;
    this.markDirty();
    return this.properties.optionByType;
  }

  @action
  changeZoomLevel(zoomLevel: number) {
    this.vizState.zoomLevel = zoomLevel;
    this.markDirty();
  }

  @action
  changeCenterPoint(centerPoint: Object) {
    this.vizState.centerPoint = { lat: centerPoint.lat(), lng: centerPoint.lng() };
    this.markDirty();
  }

  /**
   * Check if ReportPart is drilling down or not
   * @return {boolean}
   */
  isDrillingDown(): boolean {
    return this.hasDrillDown;
  }

  /**
   * Get metric container
   */
  getMetricContainer() {
    const activeSerieKey = get('fieldName', this.activeSerie);
    const pointType = get('pointType', this.activePointOption);

    if (isEmpty(activeSerieKey)) return null;

    if (
      POINTS_OPTIONS_FOR_BUBBLE_ALL.indexOf(pointType) > -1 &&
      !isEmpty(getElements(this[ContainerName.BUBBLE_VALUES], activeSerieKey))
    ) {
      return ContainerName.BUBBLE_VALUES;
    }

    if (
      POINTS_OPTIONS_FOR_SHADING.indexOf(pointType) > -1 &&
      !isEmpty(getElements(this[ContainerName.SHADING_VALUES], activeSerieKey))
    ) {
      return ContainerName.SHADING_VALUES;
    }
  }

  isMapStyle = mapStyle => {
    return this.isChartType(this.chartType, mapStyle);
  };
}

export default ReportPartMapContent;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartMapContent.js